import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { I18n } from '@/lib/api/types';
import { FieldShell, inputBase } from '@/lib/ui/Field';
import { cn } from '@/lib/ui/cn';
import { useServerLocales } from '@/lib/i18n/useServerLocales';
import { useCharLimit } from '@/lib/ui/CharCounter';

/**
 * Paired EN/AR input (spec §6.1). Per-field RTL on the AR side regardless of chrome
 * language (spec §6.3). Completeness chip. In translate-only mode the EN side is read-only.
 *
 * On a deployment whose `brand.locales` is a single locale the pair collapses to one plain
 * input: no EN/AR tab strip, no completeness chip. An Arabic tab nobody will ever fill is a
 * permanently amber "AR ·" on every string in the console, which reads as unfinished work
 * rather than as a language that is not enabled. Any Arabic already stored is carried through
 * untouched on every edit — hiding the pane must never be what deletes a translation.
 */
export function I18nField({
  label,
  value,
  onChange,
  required,
  error,
  multiline,
  translateOnly,
  disabled,
  maxLength,
}: {
  label: string;
  value: I18n | undefined;
  onChange: (v: I18n) => void;
  required?: boolean;
  error?: string | null;
  multiline?: boolean;
  translateOnly?: boolean;
  disabled?: boolean;
  /** Per-locale `maxLength` from the schema: a hard stop and an "N left" counter (D-153). */
  maxLength?: number;
}) {
  const { t } = useTranslation();
  const { showArChrome } = useServerLocales();
  const resolvedLabel = t(label, { defaultValue: label });
  const v: I18n = { en: value?.en ?? '', ar: value?.ar ?? '' };
  const [tab, setTab] = useState<'en' | 'ar'>(translateOnly ? 'ar' : 'en');
  const enComplete = !!v.en.trim();
  const arComplete = !!v.ar.trim();

  const chip = (
    <span className="flex items-center gap-[6px] font-mono text-label-sm uppercase tracking-widest">
      <span className={enComplete ? 'text-signal-teal' : 'text-ink-mute'}>EN{enComplete ? ' ✓' : ' ·'}</span>
      <span className={arComplete ? 'text-signal-teal' : 'text-signal-amber-ink'}>
        AR{arComplete ? ' ✓' : ' ·'}
      </span>
    </span>
  );

  const Tag = multiline ? 'textarea' : 'input';

  // Translate-only is an Arabic-side workflow; it cannot exist without Arabic, so the pane
  // stays whenever a caller asks for it even if the chrome would otherwise be hidden.
  const bilingual = showArChrome || translateOnly;
  const active = bilingual ? tab : 'en';
  // The counter follows the pane on screen; each locale is limited on its own.
  const limit = useCharLimit(active === 'en' ? v.en : v.ar, maxLength);
  const limitAttrs = limit
    ? {
        maxLength: limit.inputProps.maxLength,
        onPaste: limit.inputProps.onPaste,
        onBlur: limit.inputProps.onBlur,
        'aria-describedby': limit.describedBy,
      }
    : {};

  return (
    <FieldShell
      label={resolvedLabel}
      required={required}
      error={error}
      labelAdornment={bilingual ? chip : undefined}
    >
      {bilingual && (
      <div className="flex overflow-hidden rounded-sm border-[0.5px] border-hairline">
        <button
          type="button"
          onClick={() => setTab('en')}
          className={cn(
            'px-sm py-[6px] font-mono text-label uppercase tracking-widest',
            tab === 'en' ? 'bg-accent-blue text-canvas-paper' : 'bg-canvas-deep-sand text-ink-mute',
          )}
        >
          EN
        </button>
        <button
          type="button"
          onClick={() => setTab('ar')}
          className={cn(
            'px-sm py-[6px] font-mono text-label uppercase tracking-widest',
            tab === 'ar' ? 'bg-accent-blue text-canvas-paper' : 'bg-canvas-deep-sand text-ink-mute',
          )}
        >
          AR
        </button>
      </div>
      )}
      {active === 'en' ? (
        <Tag
          dir="ltr"
          lang="en"
          value={v.en}
          readOnly={translateOnly || disabled}
          {...limitAttrs}
          onChange={(e: { target: { value: string } }) => onChange({ ...v, en: e.target.value })}
          className={cn(inputBase, multiline && 'min-h-[80px] resize-y', translateOnly && 'opacity-70')}
          aria-label={t('forms.fieldEnglish', { label: resolvedLabel })}
        />
      ) : (
        <Tag
          dir="rtl"
          lang="ar"
          value={v.ar}
          readOnly={disabled}
          {...limitAttrs}
          onChange={(e: { target: { value: string } }) => onChange({ ...v, ar: e.target.value })}
          className={cn(inputBase, 'font-arabic', multiline && 'min-h-[80px] resize-y')}
          aria-label={t('forms.fieldArabic', { label: resolvedLabel })}
        />
      )}
      {limit?.counter}
    </FieldShell>
  );
}
